import React from 'react';
import {Button, Dropdown, Tag} from 'antd';
import {CloseOutlined, CaretDownOutlined} from '@ant-design/icons';
import {useTranslation} from '../../../hooks/useTranslation';

export type SortField = 'occurrenceDate' | 'externalCode' | 'grossLoss' | 'netLoss';
export type SortDir = 'asc' | 'desc';
export type GroupField = 'none' | 'lossEventType' | 'businessLine' | 'status' | 'severityTier';

export interface FilterChip {
  key: string;
  label: string;
}

interface EventsFilterBarProps {
  chips: FilterChip[];
  onRemoveChip: (key: string) => void;
  hasYtd: boolean;
  onToggleYtd: () => void;
  groupBy: GroupField;
  onGroupByChange: (val: GroupField) => void;
  sortField: SortField;
  sortDir: SortDir;
  onSortChange: (field: SortField, dir: SortDir) => void;
  onExport?: () => void;
}

const EventsFilterBar: React.FC<EventsFilterBarProps> = ({
  chips,
  onRemoveChip,
  hasYtd,
  onToggleYtd,
  groupBy,
  onGroupByChange,
  sortField,
  sortDir,
  onSortChange,
  onExport,
}) => {
  const {t} = useTranslation();

  const groupItems = (
    ['none', 'lossEventType', 'businessLine', 'status', 'severityTier'] as GroupField[]
  ).map(key => ({
    key,
    label: t(`events.groupBy.${key}`),
    onClick: () => onGroupByChange(key),
  }));

  const sortItems = (
    ['occurrenceDate', 'externalCode', 'grossLoss', 'netLoss'] as SortField[]
  ).flatMap(field => [
    {
      key: `${field}-asc`,
      label: `${t(`events.sortBy.${field}`)} ↑`,
      onClick: () => onSortChange(field, 'asc'),
    },
    {
      key: `${field}-desc`,
      label: `${t(`events.sortBy.${field}`)} ↓`,
      onClick: () => onSortChange(field, 'desc'),
    },
  ]);

  const groupLabel = groupBy !== 'none'
    ? `${t('events.groupBy.label')}: ${t(`events.groupBy.${groupBy}`)}`
    : t('events.groupBy.label');

  const sortArrow = sortDir === 'asc' ? '↑' : '↓';
  const sortLabel = `${t('events.sortBy.label')}: ${t(`events.sortBy.${sortField}`)} ${sortArrow}`;

  return (
    <div className="gt-events-filter-bar">
      <div className="gt-events-filter-chips">
        {chips.map(chip => (
          <Tag
            key={chip.key}
            closable
            onClose={() => onRemoveChip(chip.key)}
            closeIcon={<CloseOutlined style={{fontSize: 10}} />}
            className="gt-events-filter-chip"
          >
            {chip.label}
          </Tag>
        ))}

        <Tag
          className={`gt-events-filter-chip gt-events-filter-chip-ytd${hasYtd ? ' gt-events-filter-chip-ytd-active' : ''}`}
          closable={hasYtd}
          onClose={hasYtd ? onToggleYtd : undefined}
          closeIcon={hasYtd ? <CloseOutlined style={{fontSize: 10}} /> : undefined}
          onClick={!hasYtd ? onToggleYtd : undefined}
          style={{cursor: hasYtd ? 'default' : 'pointer'}}
        >
          {hasYtd ? 'YTD' : t('events.filters.addYtd')}
        </Tag>
      </div>

      <div className="gt-events-filter-actions">
        <Dropdown menu={{items: groupItems}} trigger={['click']}>
          <Button size="small" className="gt-events-ctrl-btn">
            {groupLabel} <CaretDownOutlined style={{fontSize: 10}} />
          </Button>
        </Dropdown>

        <Dropdown menu={{items: sortItems}} trigger={['click']}>
          <Button size="small" className="gt-events-ctrl-btn">
            {sortLabel} <CaretDownOutlined style={{fontSize: 10}} />
          </Button>
        </Dropdown>

        {onExport && (
          <Button size="small" type="link" className="gt-events-ctrl-export" onClick={onExport}>
            {t('events.export')}
          </Button>
        )}
      </div>
    </div>
  );
};

export default EventsFilterBar;
